import { applyCommand, type Command } from "../document/commands.ts";
import type { MapDocument, TileRef, WorldDocument } from "../document/types.ts";
import { brushSetFlags, type BrushEntry, type BrushSet, type BrushSetFlag, type BrushSetLookup } from "./brush-set-file.ts";
import { removePropsCommand, selectedProps } from "./many-props.ts";
import { frameKey, frameScatterEntry, prepareScatter, templateScatterEntry, type FrameLookups, type ScatterEntry, type ScatterKit } from "./scatter-entries.ts";
import type { ScatterPlacement } from "./scatter.ts";
import { placeTemplate, roleLayers, type PropRef, type PropTemplate } from "./templates.ts";

/**
 * The prop brush's pure parts around the scatter core (ADR-123 decisions 1.5, 4 and 6): a set prepared from the
 * entries a world can use, the others flagged and named; the pieces placements draw as ghosts; the one command a
 * stroke writes; and the props an erase takes, by their foot points, on the layers the set places on.
 */

/**
 * A set prepared for the hand: the scatter's kit over its usable entries (null when none is), those entries in the
 * set's order, each one's template (null for a frame entry), and one flag an entry of the whole set.
 */
export interface HeldKit {
  readonly kit: ScatterKit | null;
  readonly entries: readonly BrushEntry[];
  readonly templates: readonly (PropTemplate | null)[];
  readonly flags: readonly (BrushSetFlag | null)[];
}

/**
 * One piece a ghost draws: a frame, its anchor in tiles and its flips.
 */
export interface BrushGhost {
  readonly tileset: string;
  readonly tile: number;
  readonly x: number;
  readonly y: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
}

/**
 * What a stroke writes: the command, the props it places, in order, and how many props of ground cover its command
 * removes.
 */
export interface StrokeWrite {
  readonly command: Command;
  readonly props: readonly PropRef[];
  readonly cleared: number;
}

/**
 * Prepares a set for the hand, its own ground rule with it. An entry the lookup flags is left out; so is an entry the
 * catalogue cannot size, flagged as a frame the world lacks (a template whose piece cannot be sized, as a template
 * that cannot be read).
 *
 * @param set The set.
 * @param lookup The world's frames and the library's templates.
 * @param lookups The editor's frame sizes and footprints.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Authoring cells per tile.
 * @param cover The frames that are known ground cover (`knownCover`), none when left out.
 * @returns The prepared set.
 */
export function brushKit(set: BrushSet, lookup: BrushSetLookup, lookups: FrameLookups, tileSize: number, cellsPerTile = 4, cover?: ReadonlySet<string>): HeldKit {
  const flags = brushSetFlags(set, lookup);
  const scatter: ScatterEntry[] = [];
  const entries: BrushEntry[] = [];
  const templates: (PropTemplate | null)[] = [];
  set.entries.forEach((entry, i) => {
    if (flags[i] !== null) {
      return;
    }
    const rule = { weight: entry.weight, gap: entry.gap, flip: entry.flip, ...(entry.ground === undefined ? {} : { ground: entry.ground }) };
    if (entry.kind === "frame") {
      const prepared = frameScatterEntry(entry.frame, rule, lookups);
      if (prepared === null) {
        flags[i] = "frame-missing";
        return;
      }
      scatter.push(prepared);
      templates.push(null);
    } else {
      const template = lookup.template(entry.template) as PropTemplate;
      const prepared = templateScatterEntry(template, rule, lookups);
      if (prepared === null) {
        flags[i] = "template-broken";
        return;
      }
      scatter.push(prepared);
      templates.push(template);
    }
    entries.push(entry);
  });
  return { kit: scatter.length === 0 ? null : prepareScatter(scatter, tileSize, cellsPerTile, set.ground, { keepsGapFromOthers: set.keepsGapFromOthers, allowsPlacingOnTop: set.allowsPlacingOnTop, clearsGroundCover: set.clearsGroundCover, ...(cover === undefined ? {} : { cover }) }), entries, templates, flags };
}

/**
 * Why one entry is skipped, in the status bar's words.
 *
 * @param entry The entry.
 * @param flag Its flag.
 * @param frameName Names a frame as the palettes do.
 * @returns The reason.
 */
function skipReason(entry: BrushEntry, flag: BrushSetFlag, frameName: (frame: TileRef) => string): string {
  if (entry.kind === "frame") {
    return `${frameName(entry.frame)} is not in this world's packs`;
  }
  return flag === "template-missing" ? `template ${entry.template} is gone` : flag === "template-broken" ? `template ${entry.template} cannot be read` : `template ${entry.template} has a behaviour`;
}

/**
 * The first reason an entry of a set is skipped, or null when none is.
 *
 * @param set The set.
 * @param flags Its flags, one an entry.
 * @param frameName Names a frame as the palettes do.
 * @returns The reason.
 */
export function firstSkipReason(set: BrushSet, flags: readonly (BrushSetFlag | null)[], frameName: (frame: TileRef) => string): string | null {
  const index = flags.findIndex((flag) => flag !== null);
  const entry = set.entries[index];
  const flag = flags[index];
  return entry === undefined || flag === undefined || flag === null ? null : skipReason(entry, flag, frameName);
}

/**
 * What the status bar says of a set some of whose entries are skipped (decision 1.5): "2 of 6 entries skipped: Oak
 * tree is not in this world's packs".
 *
 * @param set The set.
 * @param flags Its flags, one an entry.
 * @param frameName Names a frame as the palettes do.
 * @returns The notice, or null when no entry is skipped.
 */
export function brushSkipNotice(set: BrushSet, flags: readonly (BrushSetFlag | null)[], frameName: (frame: TileRef) => string): string | null {
  const reason = firstSkipReason(set, flags, frameName);
  if (reason === null) {
    return null;
  }
  const skipped = flags.filter((flag) => flag !== null).length;
  return `${skipped} of ${set.entries.length} ${set.entries.length === 1 ? "entry" : "entries"} skipped: ${reason}`;
}

/**
 * The pieces placements draw: a frame entry's frame at the placement with its random flips, a template's pieces at
 * the anchor plus their offsets with their own flips, in placement and piece order.
 *
 * @param kit The prepared set.
 * @param placements The placements.
 * @returns The pieces.
 */
export function ghostPieces(kit: ScatterKit, placements: readonly ScatterPlacement[]): BrushGhost[] {
  const out: BrushGhost[] = [];
  appendGhostPieces(kit, placements, 0, out);
  return out;
}

/**
 * Appends the pieces of placements from an index on to a list, as `ghostPieces` lays them out, so a running
 * stroke's list grows by its new placements alone.
 *
 * @param kit The prepared set.
 * @param placements The placements.
 * @param from The first placement to lay out.
 * @param out The list, changed in place.
 */
export function appendGhostPieces(kit: ScatterKit, placements: readonly ScatterPlacement[], from: number, out: BrushGhost[]): void {
  const tileSize = kit.tileSize;
  for (let i = from; i < placements.length; i += 1) {
    const placement = placements[i] as ScatterPlacement;
    const entry = kit.entries[placement.entry];
    if (entry === undefined) {
      continue;
    }
    for (const piece of entry.pieces) {
      out.push({
        tileset: piece.frame.tileset,
        tile: piece.frame.tile,
        x: Math.round((placement.x + piece.dx / tileSize) * tileSize) / tileSize,
        y: Math.round((placement.y + piece.dy / tileSize) * tileSize) / tileSize,
        flipX: piece.flipX !== placement.flipX,
        flipY: piece.flipY !== placement.flipY,
      });
    }
  }
}

/**
 * The one command a stroke writes (decision 6.1): each frame placement an `addProp` at the end of the active layer,
 * each template placement `placeTemplate`'s parts at the placement's anchor, any role layer it adds added once, all
 * in placement order in one compound, and then one removal of the ground cover the placements cleared (A3.4), so one
 * undo restores the cover first and then takes the props away. The removal is made against the document as it is
 * now: cover that is no longer on the map, because another builder removed it meanwhile, is skipped, and so is cover
 * that has since been given a behaviour or that a reference now names.
 *
 * @param world The world.
 * @param mapId The map.
 * @param layerId The active prop layer.
 * @param kit The prepared set.
 * @param templates Each usable entry's template, null for a frame entry.
 * @param placements The stroke's placements.
 * @param allocateId Hands out a fresh id for a prefix, `prop` or `layer`.
 * @param cleared The ground cover the stroke cleared, by layer and prop.
 * @param kept The ids that anything refers to in the document now, read once at release.
 * @returns The command and the props placed, or null when there are no placements.
 * @throws Error when a command fails against the document.
 */
export function strokeCommand(world: WorldDocument, mapId: string, layerId: string, kit: ScatterKit, templates: readonly (PropTemplate | null)[], placements: readonly ScatterPlacement[], allocateId: (prefix: string) => string, cleared: readonly PropRef[] = [], kept: ReadonlySet<string> = new Set()): StrokeWrite | null {
  if (placements.length === 0) {
    return null;
  }
  const map = world.maps.find((m) => m.id === mapId);
  const counts = new Map<string, number>();
  for (const layer of map?.layers ?? []) {
    if (layer.kind === "prop") {
      counts.set(layer.id, layer.props.length);
    }
  }
  const commands: Command[] = [];
  const props: PropRef[] = [];
  let current = world;
  let applied = 0;
  for (const placement of placements) {
    const entry = kit.entries[placement.entry];
    const template = templates[placement.entry] ?? null;
    if (entry === undefined) {
      continue;
    }
    if (template === null) {
      const piece = entry.pieces[0];
      if (piece === undefined) {
        continue;
      }
      const index = counts.get(layerId) ?? 0;
      const id = allocateId("prop");
      commands.push({ kind: "addProp", mapId, layerId, index, prop: { id, frame: { tileset: piece.frame.tileset, tile: piece.frame.tile }, x: placement.x, y: placement.y, flipX: placement.flipX, flipY: placement.flipY, sortPixels: 0 } });
      counts.set(layerId, index + 1);
      props.push({ layerId, propId: id });
      continue;
    }
    for (; applied < commands.length; applied += 1) {
      current = applyCommand(current, commands[applied] as Command);
    }
    const placed = placeTemplate(current, mapId, template, { x: placement.x, y: placement.y }, allocateId);
    const parts = placed.command.kind === "compound" ? placed.command.commands : [placed.command];
    for (const part of parts) {
      commands.push(part);
      if (part.kind === "addProp") {
        counts.set(part.layerId, (counts.get(part.layerId) ?? 0) + 1);
      } else if (part.kind === "addLayer") {
        counts.set(part.layer.id, 0);
      }
    }
    props.push(...placed.props);
  }
  if (commands.length === 0) {
    return null;
  }
  const standing = map === undefined ? [] : selectedProps(map, cleared).filter(({ prop }) => prop.behaviour === undefined && !kept.has(prop.id));
  const removable = standing.map(({ layerId, prop }) => ({ layerId, propId: prop.id }));
  const removal = map === undefined || standing.length === 0 ? null : removePropsCommand(map, removable);
  if (removal !== null) {
    commands.push(removal);
  }
  return { command: { kind: "compound", commands }, props, cleared: removal === null ? 0 : standing.length };
}

/**
 * The layers an erase reaches (decision 4.3): the active layer and the role layers the set's templates place their
 * pieces on in this map, each once and only when editable (visible and unlocked).
 *
 * @param map The map.
 * @param activeId The active prop layer.
 * @param templates Each usable entry's template, null for a frame entry.
 * @param editable Whether a layer may be edited.
 * @returns The layer ids, the active first.
 */
export function eraseLayers(map: MapDocument, activeId: string, templates: readonly (PropTemplate | null)[], editable: (layerId: string) => boolean): string[] {
  const roles = roleLayers(map);
  const ids = [activeId];
  for (const template of templates) {
    for (const piece of template?.pieces ?? []) {
      const id = roles[piece.role];
      if (id !== null && !ids.includes(id)) {
        ids.push(id);
      }
    }
  }
  return ids.filter((id) => editable(id));
}

/**
 * The set's props an erase dab takes (decisions 4.1 and 4.2): a prop of a layer given whose frame, flips ignored,
 * is one the set places, and whose foot point, the bottom centre of its frame in whole pixels, is inside the circle.
 *
 * @param map The map.
 * @param kit The prepared set.
 * @param layerIds The layers the erase reaches.
 * @param centreX The dab's centre column, in cells.
 * @param centreY The dab's centre row, in cells.
 * @param radius The radius in tiles.
 * @param lookups The editor's frame sizes.
 * @returns The props, in layer and document order.
 */
export function erasableIn(map: MapDocument, kit: ScatterKit, layerIds: readonly string[], centreX: number, centreY: number, radius: number, lookups: Pick<FrameLookups, "frameSize">): PropRef[] {
  const tileSize = kit.tileSize;
  const x0 = centreX * kit.cellPx;
  const y0 = centreY * kit.cellPx;
  const limit = radius * tileSize * radius * tileSize;
  const out: PropRef[] = [];
  for (const layerId of layerIds) {
    const layer = map.layers.find((l) => l.id === layerId);
    if (layer?.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (!kit.gaps.has(frameKey(prop.frame))) {
        continue;
      }
      const width = lookups.frameSize(prop.frame)?.w ?? 0;
      const dx = Math.round(prop.x * tileSize) + Math.floor(width / 2) - x0;
      const dy = Math.round(prop.y * tileSize) + tileSize - y0;
      if (dx * dx + dy * dy <= limit) {
        out.push({ layerId, propId: prop.id });
      }
    }
  }
  return out;
}
